import { Accordion } from 'react-bootstrap';
import { isEqual, cloneDeep } from 'lodash';
import React, { PropsWithChildren } from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { useLocation, useNavigate } from 'react-router-dom';
import { plusOutlineIcon } from '@progress/kendo-svg-icons';
import { Checkbox, RadioButton } from '@progress/kendo-react-inputs';

import {
  AlgorithmStep,
  CompoundStatement,
  AlgorithmControlStructure
} from '../../types/algorithm';
import { StepEditor } from './step-editor';
import { LineWithComponentCentered } from '.';
import { uuid } from '../../types/common-helper';
import OperationSelector from './operation-selector';
import { AlgorithmButtons } from './algorithm-buttons';
import { ConditionSelector } from './condition-selector';
import { StructureStepSummary } from './summary-builder';
import { IdentifierSelector } from './identifier-selector';
import { GameDocumentContext } from '../../contexts/game-document';
import { ToastBulb } from '../map/event-configuration/event--algorithm';
import { GetTaskById, GetTaskContentById } from '../../utils/game-document';
import { ConditionalOperations } from '../../constants/conditional-operations';

interface CompoundStatementContainerProps extends PropsWithChildren {
  index?: number;
  taskId?: string;
  type: 'if' | 'and' | 'or';
  compoundStatement?: CompoundStatement;
  deleteCompound?: (index: number) => void;
  handleArgChange?: (v: string | string[]) => void;
  copyCompound?: (step: CompoundStatement) => void;
  moveCompound?: (fromIndex: number, toIndex: number) => void;
}

const compoundStatementColor = {
  if: {
    color: '#003A64',
    border: '1px solid #4698CB',
    backgroundColor: '#4698CB33'
  },
  and: {
    color: '#6262AB',
    border: '1px solid #6262AB',
    backgroundColor: '#6262AB5C'
  },
  or: {
    color: '#5B0812',
    border: '1px solid #FEB333',
    backgroundColor: '#FEB33380'
  }
};

const CompoundStatementContainer: React.FC<CompoundStatementContainerProps> = ({
  type,
  index,
  taskId,
  children,
  compoundStatement,
  moveCompound,
  copyCompound,
  deleteCompound,
  handleArgChange
}) => {
  const { state: stateLocation, pathname } = useLocation();
  const navigate = useNavigate();

  const [state] = React.useContext(GameDocumentContext);

  const selectedForm = React.useMemo(() => {
    if (!compoundStatement || !state.gameDocument || !taskId) return undefined;

    const formid = compoundStatement.argumentAssetId?.at(0);
    if (!formid) return undefined;

    const selectedTask = GetTaskById(state.gameDocument, taskId);
    if (!selectedTask || !selectedTask.taskContentId) return undefined;

    const selectedTaskContent = GetTaskContentById(
      state.gameDocument,
      selectedTask?.taskContentId ?? ''
    );
    if (!selectedTaskContent || !selectedTaskContent.forms) return undefined;

    return selectedTaskContent.forms.find((f) => f.id === formid);
  }, [
    taskId,
    compoundStatement?.operation,
    compoundStatement?.argumentAssetId
  ]);

  React.useEffect(() => {
    if (
      stateLocation?.type !== undefined ||
      stateLocation?.inCompleteStepIndex !== index
    )
      return;

    const targetElement = document.getElementById(
      `if-step-${compoundStatement?.id}`
    );
    if (targetElement) {
      targetElement.classList.add('blip');
      setTimeout(() => {
        targetElement.classList.remove('blip');
      }, 1000);
      targetElement.scrollIntoView({
        behavior: 'smooth'
      });
      navigate(pathname, { replace: true });
    }
  }, [
    index,
    compoundStatement?.id,
    stateLocation?.type,
    stateLocation?.inCompleteStepIndex
  ]);

  return (
    <div
      id={`if-step-${compoundStatement?.id}`}
      className={`compound-statement-step ${type}`}>
      <div
        style={{
          gap: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
        <div
          style={{
            gap: '16px',
            display: 'flex',
            overflow: 'hidden'
          }}>
          <div
            style={{
              flexShrink: 0,
              paddingTop: '12px'
            }}>
            <span
              style={{
                flexShrink: 0,
                width: '40px',
                height: '40px',
                display: 'flex',
                fontWeight: '600',
                borderRadius: type === 'if' ? '8px' : '100%',
                alignItems: 'center',
                justifyContent: 'center',
                ...compoundStatementColor[type]
              }}>
              {type.toUpperCase()}
            </span>
          </div>

          <div
            className="d-flex flex-wrap if"
            style={{
              gap: '16px'
            }}>
            {children}
          </div>
        </div>

        {type !== 'if' &&
          index !== undefined &&
          compoundStatement !== undefined &&
          moveCompound !== undefined &&
          copyCompound !== undefined &&
          deleteCompound !== undefined && (
            <AlgorithmButtons
              handleCopy={() => copyCompound(compoundStatement)}
              handleMoveUp={() => moveCompound(index, index - 1)}
              handleMoveDown={() => moveCompound(index, index + 1)}
              handleDeleteStep={() => deleteCompound(index)}
            />
          )}
      </div>

      {compoundStatement &&
        compoundStatement.operation === 'choosesAnswer' &&
        compoundStatement.argumentAssetId &&
        selectedForm &&
        handleArgChange && (
          <div className={'d-flex flex-column gap-2 p-3 bg-white'}>
            <span
              className={
                'text-primary fw-bold cursor-pointer text-decoration-none'
              }
              style={{ width: 'fit-content' }}
              onClick={() =>
                handleArgChange([
                  (compoundStatement.argumentAssetId as string[])[0]
                ])
              }>
              Clear
            </span>
            <div className={'d-flex gap-3'}>
              {selectedForm.answers?.map((a) =>
                selectedForm.type === 'checkbox' ? (
                  <Checkbox
                    key={a.answerResId}
                    size={'large'}
                    label={a.description}
                    value={a.answerResId}
                    checked={compoundStatement.argumentAssetId?.includes(
                      a.answerResId
                    )}
                    onChange={(e) => {
                      let result = cloneDeep(
                        compoundStatement.argumentAssetId
                      ) as string[];
                      if (e.value) {
                        result = [...result, a.answerResId];
                      } else {
                        const idx = result.findIndex(
                          (r) => r === a.answerResId
                        );
                        result.splice(idx, 1);
                      }

                      handleArgChange(result);
                    }}
                  />
                ) : (
                  <span key={a.answerResId}>
                    <RadioButton
                      size={'large'}
                      name={`${compoundStatement.id}-answer`}
                      label={a.description}
                      value={a.answerResId}
                      checked={compoundStatement.argumentAssetId?.includes(
                        a.answerResId
                      )}
                      onChange={(e) => {
                        const result = cloneDeep(
                          compoundStatement.argumentAssetId
                        ) as string[];
                        result[1] = a.answerResId;
                        handleArgChange(result);
                      }}
                    />
                  </span>
                )
              )}
            </div>
          </div>
        )}
    </div>
  );
};

const AndOrButtons: React.FC<{
  andClick?: () => void;
  orClick?: () => void;
}> = ({ andClick, orClick }) => {
  return (
    <LineWithComponentCentered>
      <div className={'d-flex gap-2 my-2'}>
        {andClick && (
          <Button
            size={'small'}
            style={{
              backgroundColor: 'white',
              color: '#003A64',
              border: '1px solid #003A64'
            }}
            fillMode={'outline'}
            className={'d-flex align-items-center'}
            onClick={andClick}>
            <SvgIcon
              icon={plusOutlineIcon}
              className={'mr-2'}
              style={{
                color: '#5856D6'
              }}
            />
            AND
          </Button>
        )}
        {orClick && (
          <Button
            size={'small'}
            style={{
              backgroundColor: 'white',
              color: '#003A64',
              border: '1px solid #003A64'
            }}
            fillMode={'outline'}
            className={'d-flex align-items-center'}
            onClick={orClick}>
            <SvgIcon icon={plusOutlineIcon} className={'mr-2 text-warning'} />
            OR
          </Button>
        )}
      </div>
    </LineWithComponentCentered>
  );
};

export const ControlStructureEditor = (props: AlgorithmControlStructure) => {
  const {
    action,
    taskId,
    defaultExpanded,
    ifSteps = [],
    elseSteps = [],
    ifCompoundStatements = [],
    onCopy,
    onDelete,
    onMoveUp,
    onMoveDown,
    onChangeStructure
  } = props;

  const [localIfCompounds, setLocalIfCompounds] =
    React.useState(ifCompoundStatements);
  const [localIfSteps, setLocalIfSteps] = React.useState(ifSteps);
  const [localElseSteps, setLocalElseSteps] = React.useState(elseSteps);
  const [selectedIdentifier, setSelectedIdentifier] = React.useState(
    props.identifier
  );

  const conditionalOperationList = React.useMemo(() => {
    if (!props.condition || props.condition === 'select')
      return ConditionalOperations.filter(
        (item) => !item.availableOnConditions
      );

    let filtered = ConditionalOperations.filter((item) =>
      props.condition
        ? item.availableOnConditions?.includes(props.condition)
        : !item.availableOnConditions
    );

    if (props.condition && !filtered.length) {
      filtered = ConditionalOperations.filter(
        (item) => !item.availableOnConditions
      );
    }

    return filtered.filter((a) =>
      a.availableOnActions
        ? a.availableOnActions?.includes(props.action ?? '')
        : !a.availableOnActions
    );
  }, [props.condition, props.action]);

  const generateIfCompound = React.useCallback(
    (condition?: string) => {
      let filtered = ConditionalOperations.filter((item) =>
        condition
          ? item.availableOnConditions?.includes(condition)
          : !item.availableOnConditions
      );

      if (condition && !filtered.length) {
        filtered = ConditionalOperations.filter(
          (item) => !item.availableOnConditions
        );
      }

      return filtered.filter((a) =>
        a.availableOnActions
          ? a.availableOnActions?.includes(props.action ?? '')
          : !a.availableOnActions
      );
    },
    [props.action]
  );

  React.useEffect(() => {
    setLocalIfSteps(props.ifSteps ?? []);
    setLocalElseSteps(props.elseSteps ?? []);
    setLocalIfCompounds(props.ifCompoundStatements ?? []);
  }, []);

  React.useEffect(() => {
    if (!isEqual(props, { ...props, ifSteps, elseSteps })) {
      setLocalIfSteps(props.ifSteps ?? []);
      setLocalElseSteps(props.elseSteps ?? []);
      setLocalIfCompounds(props.ifCompoundStatements ?? []);
    }
  }, [props]);

  /** Emit change */
  const emitChange = React.useCallback(
    (data: AlgorithmControlStructure) => {
      onChangeStructure?.(data);
    },
    [onChangeStructure]
  );

  const update = React.useCallback(
    (partial: Partial<AlgorithmControlStructure>) => {
      let next = { ...props, ...partial };
      if (
        partial.identifier &&
        partial.identifier === 'score' &&
        ['player', 'team'].includes(props.identifier ?? '')
      ) {
        next.condition = 'select';
        delete next.operation;
        delete next.argumentAssetId;
        delete next.argumentQuantity;
      }

      if (partial.operation) {
        delete next.argumentAssetId;
        delete next.argumentQuantity;
      }
      emitChange(next);
    },
    [props, emitChange]
  );

  const addIfStep = (index: number) => {
    const next = cloneDeep(localIfSteps);
    next.splice(index, 0, { id: uuid() });
    setLocalIfSteps(next);
    update({ ifSteps: next });
  };

  const addElseStep = (index: number) => {
    const next = cloneDeep(localElseSteps);
    next.splice(index, 0, { id: uuid() });
    setLocalElseSteps(next);
    update({ elseSteps: next });
  };

  const handleIfStepChange = (step: AlgorithmStep) => {
    const next = localIfSteps.map((s) => (s.id === step.id ? step : s));
    setLocalIfSteps(next);
    update({ ifSteps: next });
  };

  const handleElseStepChange = (step: AlgorithmStep) => {
    const next = localElseSteps.map((s) => (s.id === step.id ? step : s));
    setLocalElseSteps(next);
    update({ elseSteps: next });
  };

  const handleDeleteIfStep = (step: AlgorithmStep) => {
    const next = localIfSteps.filter((s) => s.id !== step.id);
    setLocalIfSteps(next);
    update({ ifSteps: next });
  };

  const handleDeleteElseStep = (step: AlgorithmStep) => {
    const next = localElseSteps.filter((s) => s.id !== step.id);
    setLocalElseSteps(next);
    update({ elseSteps: next });
  };

  const handleCopyIfStep = (step: AlgorithmStep) => {
    const next = cloneDeep(localIfSteps);
    const copy = cloneDeep(step);
    copy.id = uuid();
    next.push(copy);
    setLocalIfSteps(next);
    update({ ifSteps: next });
  };

  const handleCopyElseStep = (step: AlgorithmStep) => {
    const next = cloneDeep(localElseSteps);
    const copy = cloneDeep(step);
    copy.id = uuid();
    next.push(copy);
    setLocalElseSteps(next);
    update({ elseSteps: next });
  };

  const moveStep = (type: 'IF' | 'ELSE', step: AlgorithmStep, dir: -1 | 1) => {
    const list = type === 'IF' ? localIfSteps : localElseSteps;
    const index = list.findIndex((s) => s.id === step.id);
    if (index < 0) return;

    const to = index + dir;
    if (to < 0 || to >= list.length) return;

    const next = cloneDeep(list);
    const [item] = next.splice(index, 1);
    next.splice(to, 0, item);

    if (type === 'IF') {
      setLocalIfSteps(next);
      update({ ifSteps: next });
    } else {
      setLocalElseSteps(next);
      update({ elseSteps: next });
    }
  };

  const addIfConjunction = () => {
    const next = [
      ...localIfCompounds,
      { id: uuid(), type: 'and' }
    ] as CompoundStatement[];
    setLocalIfCompounds(next);
    update({ ifCompoundStatements: next });
  };

  const addIfDisjunction = () => {
    const next = [
      ...localIfCompounds,
      { id: uuid(), type: 'or' }
    ] as CompoundStatement[];
    setLocalIfCompounds(next);
    update({ ifCompoundStatements: next });
  };

  const updateCompound = (index: number, patch: Partial<CompoundStatement>) => {
    const next = localIfCompounds.map((s, i) => {
      if (i === index) {
        let result = { ...s, ...patch };
        if (patch.identifier)
          result = {
            id: s.id,
            type: s.type,
            ...patch
          };

        if (patch.operation) {
          delete result.argumentAssetId;
          delete result.argumentQuantity;
        }
        return result;
      }
      return s;
    });
    setLocalIfCompounds(next);
    update({ ifCompoundStatements: next });
  };

  const deleteCompound = (index: number) => {
    const next = localIfCompounds.filter((_, i) => i !== index);
    setLocalIfCompounds(next);
    update({ ifCompoundStatements: next });
  };

  const copyCompound = (step: CompoundStatement) => {
    const next = cloneDeep(localIfCompounds);
    const clone = cloneDeep(step);
    clone.id = uuid();
    next.push(clone);
    setLocalIfCompounds(next);
    update({ ifCompoundStatements: next });
  };

  const moveCompound = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= localIfCompounds.length) return;

    const next = cloneDeep(localIfCompounds);
    const [item] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, item);

    setLocalIfCompounds(next);
    update({ ifCompoundStatements: next });
  };

  const handleIdentifierChange = (v: string) => {
    setSelectedIdentifier(v);
    update({ identifier: v });
  };

  const handleConditionChange = (v: string) => update({ condition: v });
  const handleOperationChange = (v: string) => update({ operation: v });
  const handleArgChange = (v: string | string[]) =>
    update({ argumentAssetId: v });
  const handleQtyChange = (v: number) => update({ argumentQuantity: v });
  const handleCheckboxChange = (v: boolean) =>
    update({
      argumentBoolean: v,
      useFallbackValue:
        props.operation === 'giveScore(score)' ? v : props.useFallbackValue
    });

  return (
    <Accordion
      className={'algorithm-accordion'}
      defaultActiveKey={defaultExpanded ? '0' : undefined}>
      <Accordion.Item eventKey={'0'}>
        <Accordion.Header className={'structure'}>
          <div
            className={
              'algorithm-header d-flex align-items-center justify-content-between w-100 gap-3'
            }>
            <div
              className={
                'algorithm-header-left d-flex gap-3 align-items-center'
              }>
              <div
                className={'algorithm-label d-flex gap-1 align-items-center'}
                style={{ fontWeight: 'bold', fontSize: '24px' }}>
                <span className={'material-symbols-outlined'}>fork_right</span>
                <span>Rule</span>
              </div>

              <StructureStepSummary step={props} />
            </div>
            <AlgorithmButtons
              handleCopy={() => onCopy?.(props)}
              handleMoveUp={() => onMoveUp?.(props)}
              handleMoveDown={() => onMoveDown?.(props)}
              handleDeleteStep={() => onDelete?.(props)}
            />
          </div>
        </Accordion.Header>

        <Accordion.Body>
          <div className="step-editor step-editor--control">
            <ToastBulb
              text={
                "Add conditions that limit when the action can run. You don't need to add a rule if the action should always run when the task is completed."
              }
              bgColor={'#6262AB1A'}
            />

            <div
              className={'d-flex gap-1 align-items-center'}
              style={{
                fontWeight: 'bold',
                fontSize: '16px'
              }}>
              <span className={'material-symbols-outlined'}>fork_right</span>
              <span>IF</span>
            </div>

            <ToastBulb
              text={`An IF statement defines what must happen before the action is triggered.
                Use AND when all of the conditions must be true. Use OR when any one of the conditions can be true.`}
              bgColor={'#6262AB1A'}
            />

            <CompoundStatementContainer
              type="if"
              index={-1}
              taskId={taskId}
              compoundStatement={{
                id: props.id,
                identifier: props.identifier,
                condition: props.condition,
                operation: props.operation,
                argumentAssetId: props.argumentAssetId
              }}
              handleArgChange={handleArgChange}>
              <IdentifierSelector
                action={props.action}
                identifier={props.identifier}
                onChange={handleIdentifierChange}
                isOnConditional
              />

              {props.identifier && (
                <ConditionSelector
                  condition={props.condition}
                  selectedIdentifier={selectedIdentifier}
                  onChange={handleConditionChange}
                />
              )}

              {props.condition !== 'select' && (
                <OperationSelector
                  taskId={taskId}
                  operation={props.operation}
                  argumentAssetId={props.argumentAssetId}
                  argumentQuantity={props.argumentQuantity}
                  operationsList={conditionalOperationList}
                  onChangeArgument={handleArgChange}
                  onChangeQuantity={handleQtyChange}
                  onChangeCheckbox={handleCheckboxChange}
                  onChangeOperation={handleOperationChange}
                />
              )}
            </CompoundStatementContainer>

            {localIfCompounds.map((compoundStatement, index) => (
              <CompoundStatementContainer
                index={index}
                taskId={taskId}
                key={compoundStatement.id}
                type={compoundStatement.type!}
                compoundStatement={compoundStatement}
                moveCompound={moveCompound}
                copyCompound={copyCompound}
                deleteCompound={deleteCompound}
                handleArgChange={(v) =>
                  updateCompound(index, { argumentAssetId: v as string })
                }>
                <IdentifierSelector
                  action={props.action}
                  identifier={compoundStatement.identifier}
                  onChange={(v) => updateCompound(index, { identifier: v })}
                  isOnConditional
                />

                {compoundStatement.identifier && (
                  <ConditionSelector
                    key={compoundStatement.id}
                    condition={compoundStatement.condition}
                    selectedIdentifier={compoundStatement.identifier}
                    onChange={(v) => updateCompound(index, { condition: v })}
                  />
                )}

                {compoundStatement.condition && (
                  <OperationSelector
                    taskId={taskId}
                    operationsList={generateIfCompound(
                      compoundStatement.condition
                    )}
                    operation={compoundStatement.operation}
                    argumentAssetId={compoundStatement.argumentAssetId}
                    argumentQuantity={compoundStatement.argumentQuantity}
                    onChangeOperation={(v) =>
                      updateCompound(index, { operation: v })
                    }
                    onChangeArgument={(v) =>
                      updateCompound(index, { argumentAssetId: v as string })
                    }
                    onChangeQuantity={(v) =>
                      updateCompound(index, { argumentQuantity: v })
                    }
                  />
                )}
              </CompoundStatementContainer>
            ))}

            <AndOrButtons
              andClick={addIfConjunction}
              orClick={addIfDisjunction}
            />

            <div
              className={'d-flex gap-1 align-items-center mt-2'}
              style={{
                fontWeight: 'bold',
                fontSize: '16px'
              }}>
              <span className={'material-symbols-outlined'}>fork_right</span>
              <span>If the criteria are met, what should happen?</span>
            </div>

            <ToastBulb
              text={`Now choose what should happen if the IF conditions are met. These actions happen only when the IF conditions are met. If you want several things to happen, select + AND.`}
              bgColor={'#6262AB1A'}
            />

            {localIfSteps.map((step, index) => (
              <StepEditor
                {...step}
                idx={index}
                key={step.id}
                type={'then'}
                taskId={taskId}
                action={action}
                isAND={index !== 0}
                onCopy={handleCopyIfStep}
                onChange={handleIfStepChange}
                onDelete={handleDeleteIfStep}
                onMoveUp={(s) => moveStep('IF', s, -1)}
                onMoveDown={(s) => moveStep('IF', s, 1)}
              />
            ))}

            <AndOrButtons andClick={() => addIfStep(localIfSteps.length + 1)} />

            <div
              className={'d-flex gap-1 align-items-center mt-2'}
              style={{
                fontWeight: 'bold',
                fontSize: '16px'
              }}>
              <span className={'material-symbols-outlined'}>fork_right</span>
              <span>If none of the criteria are met, what should happen?</span>
            </div>

            <ToastBulb
              text={`Now choose what should happen if the IF conditions are not met. These actions happen only when the IF conditions are NOT met. If you want several things to happen, select + AND.`}
              bgColor={'#6262AB1A'}
            />

            {localElseSteps.map((step, index) => (
              <StepEditor
                {...step}
                idx={index}
                key={step.id}
                type={'else'}
                taskId={taskId}
                action={action}
                isAND={index !== 0}
                onCopy={handleCopyElseStep}
                onChange={handleElseStepChange}
                onDelete={handleDeleteElseStep}
                onMoveUp={(s) => moveStep('ELSE', s, -1)}
                onMoveDown={(s) => moveStep('ELSE', s, 1)}
              />
            ))}

            <AndOrButtons
              andClick={() => addElseStep(localElseSteps.length + 1)}
            />
          </div>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};
